import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import { theme } from '../../theme/theme';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { developmentInfo, formatPropertyNumber } from '../../utils/propertyUtils';
import {
  Property,
  PropertyInvoiceWithRelations,
  useGetUserByIdQuery,
  usePatchPropertyMutation,
  usePatchPropertyInvoicesByIdMutation,
} from '../../redux/residentPortalApi';
import LoadingCover from '../common/LoadingCover';
import developmentsData from '../../resident-portal';
import { useIsHoa } from '../../hooks/useIsHoa';
import ResponsiveRecordCard from '../common/ResponsiveRecordCard';

interface OwnProps {
  property: Property;
  invoices: PropertyInvoiceWithRelations[];
}

const useStyles = makeStyles({
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    maxHeight: '80vh',
    overflowY: 'scroll',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  logo: {
    width: '100px',
  },
  invoiceDetails: {
    textAlign: 'right',
  },
  table: {
    marginTop: theme.spacing(3),
  },
  bankDetails: {
    marginTop: theme.spacing(3),
    textAlign: 'left',
  },
});

const PadelCourtInvoicesList: React.FC<OwnProps> = ({ property, invoices }) => {
  const classes = useStyles();
  const isHoa = useIsHoa();
  const showCards = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [editBalanceOpen, setEditBalanceOpen] = useState(false);
  const [padelBalance, setPadelBalance] = useState<number>(
    (property as any)?.pedalCourtInvoiceBalance ?? 0
  );
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  const [editInvoiceBalance, setEditInvoiceBalance] = useState<number | null>(null);
  const [editInvoiceStatus, setEditInvoiceStatus] = useState<string>('');
  const [editingInvoice, setEditingInvoice] = useState<PropertyInvoiceWithRelations | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [patchProperty] = usePatchPropertyMutation();
  const [patchPropertyInvoice] = usePatchPropertyInvoicesByIdMutation();

  const handleOpen = (invoice: any) => {
    setSelectedInvoice(invoice);
    setOpen(true);
  };

  const development = developmentsData.developments.find(
    (dev) => dev.name === property?.development
  );

  const owner1Id = property?.ownerIds?.[0] || null;
  const owner2Id = property?.ownerIds?.[1] || null;

  const { data: owner1 } = useGetUserByIdQuery(
    { id: owner1Id || '' },
    { skip: !owner1Id }
  );

  const { data: owner2 } = useGetUserByIdQuery(
    { id: owner2Id || '' },
    { skip: !owner2Id }
  );

  const owners = [];
  if (owner1) owners.push(owner1);
  if (owner2) owners.push(owner2);

  const handleOpenEditBalance = () => {
    setPadelBalance((property as any)?.pedalCourtInvoiceBalance ?? 0);
    setEditBalanceOpen(true);
  };

  const handleCloseEditBalance = () => {
    setEditBalanceOpen(false);
  };

  const handleSaveBalance = async () => {
    try {
      await patchProperty({
        propertyPartial: {
          id: property.id,
          pedalCourtInvoiceBalance: padelBalance,
        } as any,
      }).unwrap();
      handleCloseEditBalance();
    } catch (error) {
      console.error('Failed to update padel court invoice balance:', error);
    }
  };

  const handleOpenEditInvoice = (invoice: PropertyInvoiceWithRelations) => {
    setEditingInvoice(invoice);
    setEditInvoiceBalance(invoice.propertyInvoiceBalance ?? 0);
    setEditInvoiceStatus(invoice.status ?? 'Unpaid');
    setEditInvoiceOpen(true);
  };

  const handleCloseEditInvoice = () => {
    setEditInvoiceOpen(false);
    setEditingInvoice(null);
  };

  const handleSaveInvoice = async () => {
    if (editingInvoice?.id) {
      try {
        await patchPropertyInvoice({
          id: editingInvoice.id,
          propertyInvoicePartial: {
            propertyInvoiceBalance: editInvoiceBalance ?? 0,
            status: editInvoiceStatus,
          },
        }).unwrap();
        handleCloseEditInvoice();
      } catch (error) {
        console.error('Failed to update invoice:', error);
      }
    }
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    setPage(0);
  }, [invoices.length]);

  const paginatedInvoices = useMemo(() => {
    const start = page * rowsPerPage;
    return invoices.slice(start, start + rowsPerPage);
  }, [invoices, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderInvoiceStatus = (status?: string) =>
    status === 'Paid'
      ? 'Paid'
      : status === 'Unpaid'
      ? 'Unpaid'
      : status === 'Cancelled'
      ? 'Cancelled'
      : 'Partially Paid';

  const getInvoiceStatusStyles = (status?: string) => ({
    backgroundColor:
      status === 'Paid'
        ? '#4CAF50'
        : status === 'Unpaid'
        ? '#f44336'
        : status === 'Cancelled'
        ? '#6b7280'
        : '#FFBF00',
    color: '#fff',
  });

  const currentPadelBalance = (property as any)?.pedalCourtInvoiceBalance ?? 0;

  return invoices && development ? (
    <Box>
      {/* Padel Court Invoice Balance Section */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Padel Court Invoice Balance
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: currentPadelBalance < 0 ? '#f44336' : 'inherit',
              }}
            >
              ${currentPadelBalance.toFixed(2)}
            </Typography>
          </Box>
          {isHoa && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleOpenEditBalance}
            >
              Edit Balance
            </Button>
          )}
        </Box>
      </Paper>

      {invoices.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1">No Padel Court invoices found for this property.</Typography>
        </Box>
      ) : showCards ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {paginatedInvoices.map((invoice: PropertyInvoiceWithRelations) => {
            const isOverdue =
              new Date(invoice?.dueDate ?? '') < new Date() && invoice?.status !== 'Paid';

            return (
              <ResponsiveRecordCard
                key={invoice.invoiceNumber}
                eyebrow="Padel Court Invoice"
                title={invoice.invoiceNumber}
                badge={
                  <Chip
                    label={renderInvoiceStatus(invoice?.status)}
                    sx={getInvoiceStatusStyles(invoice?.status)}
                  />
                }
                fields={[
                  {
                    label: 'Invoice Amount',
                    value: (
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        ${(invoice?.invoice?.totalCost ?? 0).toFixed(2)}
                      </Typography>
                    ),
                  },
                  {
                    label: 'Invoice Balance',
                    value: (
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        ${(invoice?.propertyInvoiceBalance ?? 0).toFixed(2)}
                      </Typography>
                    ),
                  },
                  {
                    label: 'Due Date',
                    value: (
                      <Typography
                        sx={{ color: isOverdue ? 'lightcoral' : '#4CAF50', fontWeight: 700 }}
                      >
                        {invoice?.invoice?.dueDate
                          ? new Date(invoice.invoice.dueDate).toLocaleDateString('en-GB', {
                              timeZone: 'UTC',
                            })
                          : 'N/A'}
                      </Typography>
                    ),
                  },
                  {
                    label: 'Description',
                    value: (
                      <Typography sx={{ fontWeight: 600 }}>
                        {invoice?.invoice?.invoiceDescription || 'Padel Court Invoice'}
                      </Typography>
                    ),
                  },
                ]}
                actions={
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpen(invoice)}
                    >
                      View
                    </Button>
                    {isHoa && (
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditInvoice(invoice)}
                      >
                        Edit
                      </Button>
                    )}
                  </>
                }
              />
            );
          })}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Invoice Number</TableCell>
                <TableCell>Invoice Amount</TableCell>
                <TableCell>Invoice Balance</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInvoices.map((invoice: PropertyInvoiceWithRelations) => {
                const isOverdue =
                  new Date(invoice?.dueDate ?? '') < new Date() && invoice?.status !== 'Paid';

                return (
                  <TableRow key={invoice.invoiceNumber}>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>${(invoice?.invoice?.totalCost ?? 0).toFixed(2)}</TableCell>
                    <TableCell>${(invoice?.propertyInvoiceBalance ?? 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{ color: isOverdue ? 'lightcoral' : '#4CAF50', fontWeight: 700 }}
                      >
                        {invoice?.invoice?.dueDate
                          ? new Date(invoice.invoice.dueDate).toLocaleDateString('en-GB', {
                              timeZone: 'UTC',
                            })
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={renderInvoiceStatus(invoice?.status)}
                        sx={getInvoiceStatusStyles(invoice?.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpen(invoice)} size="small">
                        <VisibilityIcon />
                      </IconButton>
                      {isHoa && (
                        <IconButton
                          onClick={() => handleOpenEditInvoice(invoice)}
                          size="small"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {invoices.length > 0 && (
        <TablePagination
          component="div"
          count={invoices.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}

      {/* Edit Property Padel Court Balance Dialog */}
      <Dialog open={editBalanceOpen} onClose={handleCloseEditBalance}>
        <DialogTitle>Edit Padel Court Invoice Balance</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Padel Court Balance ($)"
            type="number"
            fullWidth
            variant="outlined"
            value={padelBalance}
            onChange={(e) => setPadelBalance(parseFloat(e.target.value) || 0)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditBalance}>Cancel</Button>
          <Button onClick={handleSaveBalance} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editInvoiceOpen} onClose={handleCloseEditInvoice}>
        <DialogTitle>Edit Invoice #{editingInvoice?.invoiceNumber}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Invoice Balance ($)"
            type="number"
            fullWidth
            variant="outlined"
            value={editInvoiceBalance ?? ''}
            onChange={(e) =>
              setEditInvoiceBalance(e.target.value === '' ? null : parseFloat(e.target.value))
            }
            sx={{ mt: 2, mb: 2 }}
          />
          <Select
            fullWidth
            value={editInvoiceStatus}
            onChange={(e) => setEditInvoiceStatus(e.target.value)}
            variant="outlined"
          >
            <MenuItem value="Unpaid">Unpaid</MenuItem>
            <MenuItem value="Partially Paid">Partially Paid</MenuItem>
            <MenuItem value="Paid">Paid</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditInvoice}>Cancel</Button>
          <Button onClick={handleSaveInvoice} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box className={classes.modalStyle}>
          {selectedInvoice && (
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
              <Box className={classes.header}>
                <Box className={classes.logoSection}>
                  <CardMedia
                    component="img"
                    image={developmentInfo(property.development, property.phase, property.type).logo}
                    alt="Logo"
                    className={classes.logo}
                  />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    <strong>{developmentInfo(property.development, property.phase, property.type).name}</strong>
                  </Typography>
                </Box>
                <Box className={classes.invoiceDetails}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    INVOICE
                  </Typography>
                  <Typography variant="body2">Invoice #: {selectedInvoice.invoiceNumber}</Typography>
                  <Typography variant="body2">
                    Date:{' '}
                    {selectedInvoice.refDate
                      ? new Date(selectedInvoice.refDate).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    Due Date:{' '}
                    {selectedInvoice.invoice?.dueDate
                      ? new Date(selectedInvoice.invoice.dueDate).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Bill To:
                  </Typography>
                  <Typography variant="body2">
                    {owners[0]?.name ? `${owners[0].name}` : ''}
                  </Typography>
                  <Typography variant="body2">
                    {property?.unitNumber} {property?.development}
                  </Typography>
                  <Typography variant="body2">
                    {property?.type} {property?.phase}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Payment Status:
                  </Typography>
                  <Chip
                    label={renderInvoiceStatus(selectedInvoice.status)}
                    sx={getInvoiceStatusStyles(selectedInvoice.status)}
                  />
                </Box>
              </Box>
              <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price ($)</TableCell>
                      <TableCell align="right">Total ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoice.invoice?.items?.map((item: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{Number(item.unitPrice).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {(Number(item.quantity) * Number(item.unitPrice)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography variant="h6">
                  Total: ${(selectedInvoice.invoice?.totalCost ?? 0).toFixed(2)}
                </Typography>
                <Typography variant="h6" color="primary">
                  Balance Due: ${(selectedInvoice.propertyInvoiceBalance ?? 0).toFixed(2)}
                </Typography>
              </Box>
              {development?.bank && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box className={classes.bankDetails}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Banking Details:
                    </Typography>
                    <Typography variant="body2">
                      Bank Name: {development.bank.bank}
                    </Typography>
                    <Typography variant="body2">
                      Account Name: {development.bank.accountName}
                    </Typography>
                    <Typography variant="body2">
                      Account Number: {development.bank.accountNumber}
                    </Typography>
                    <Typography variant="body2">
                      Branch Code: {development.bank.bankCode}
                    </Typography>
                    <Typography variant="body2">
                      Swift Code: {development.bank.swiftCode}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Box>
      </Modal>
    </Box>
  ) : (
    <LoadingCover />
  );
};

export default PadelCourtInvoicesList;
